import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, User, Loader2, ChevronDown } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo Exevori mis à jour
const LOGO = "https://customer-assets.emergentagent.com/job_connected-work/artifacts/kalrhzgp_remove.photos-removed-background%20Rxevori.png";

// Coordonnées correctes
const PHONE = "(581) 700-4171";
const EMAIL = "contact@exevori.com";

const getGreeting = (lang) => lang === 'fr'
  ? "Bonjour ! Je suis l'assistant virtuel de Exevori. Comment puis-je vous aider aujourd'hui ?\n\nJe peux vous renseigner sur nos services, vous aider à définir votre projet ou vous mettre en contact avec notre équipe."
  : "Hello! I'm Exevori's virtual assistant. How can I help you today?\n\nI can tell you about our services, help you define your project, or connect you with our team.";

const QUICK_QUESTIONS_FR = [
  "Quels services offrez-vous ?",
  "Combien coûte un site web ?",
  "Quel est votre processus ?",
  "Comment vous contacter ?",
];
const QUICK_QUESTIONS_EN = [
  "What services do you offer?",
  "How much does a website cost?",
  "What is your process?",
  "How can I contact you?",
];

// Generate a persistent session ID per browser session
const getSessionId = () => {
  let sid = sessionStorage.getItem('exevori_chat_sid');
  if (!sid) {
    sid = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('exevori_chat_sid', sid);
  }
  return sid;
};

export default function ChatWidget() {
  const { lang } = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Init greeting on open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', text: getGreeting(lang), id: 'greeting' }]);
    }
  }, [isOpen, lang]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', text: msg, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
        lang,
      });
      const data = res.data;
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        text: data.response, 
        id: Date.now() + 1,
        appointmentBooked: data.appointment_booked 
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: lang === 'fr'
          ? `Désolé, une erreur s'est produite. Veuillez nous contacter directement au ${PHONE} ou par courriel à ${EMAIL}.`
          : `Sorry, an error occurred. Please contact us directly at ${PHONE} or by email at ${EMAIL}.`,
        id: Date.now() + 1,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = lang === 'fr' ? QUICK_QUESTIONS_FR : QUICK_QUESTIONS_EN;

  return (
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chat-window"
          className="w-80 sm:w-96 rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background: '#0D1F1F',
            border: '1px solid rgba(79,142,247,0.2)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(79,142,247,0.1)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-5 py-4"
            style={{ background: 'linear-gradient(135deg, #152E2E, #1A2A3A)' }}
          >
            <div className="relative flex-shrink-0">
              <img src={LOGO} alt="Exevori" className="w-9 h-9 rounded-xl object-cover" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#152E2E]" />
            </div>
            <div className="flex-1">
              <div className="font-sora font-bold text-white text-sm">Exevori Assistant</div>
              <div className="text-green-400 text-xs font-inter flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                {lang === 'fr' ? 'En ligne' : 'Online'}
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            className="h-72 overflow-y-auto px-4 py-4 space-y-3"
            style={{ background: '#0A1A1A' }}
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div
                    className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center mt-1"
                    style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                  >
                    <Bot size={12} className="text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm font-inter leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'text-white rounded-tr-sm'
                      : 'text-gray-200 rounded-tl-sm'
                  }`}
                  style={
                    msg.role === 'user'
                      ? { background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }
                      : { background: '#152E2E', border: '1px solid rgba(255,255,255,0.06)' }
                  }
                >
                  {msg.text}
                  {msg.appointmentBooked && (
                    <div className="mt-2 pt-2 border-t border-white/10 text-xs text-green-400 flex items-center gap-1">
                      <span>✓</span>
                      <span>{lang === 'fr' ? 'RDV confirmé — email envoyé !' : 'Appointment confirmed — email sent!'}</span>
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div
                    className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center mt-1"
                    style={{ background: '#1A3A3A' }}
                  >
                    <User size={12} className="text-gray-300" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 justify-start">
                <div
                  className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center mt-1"
                  style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                >
                  <Bot size={12} className="text-white" />
                </div>
                <div
                  className="px-4 py-3 rounded-2xl rounded-tl-sm flex items-center gap-1"
                  style={{ background: '#152E2E', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-exevori-primary animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick questions */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 pb-2" style={{ background: '#0A1A1A' }}>
              <div className="text-xs text-gray-500 font-inter mb-2">
                {lang === 'fr' ? 'Questions fréquentes :' : 'Quick questions:'}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    data-testid={`quick-q-${i}`}
                    onClick={() => sendMessage(q)}
                    className="px-3 py-1.5 text-xs rounded-full font-inter text-exevori-primary hover:text-white transition-colors"
                    style={{ background: 'rgba(79,142,247,0.1)', border: '1px solid rgba(79,142,247,0.2)' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div
            className="px-4 py-3 border-t"
            style={{ background: '#0D1F1F', borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <div
              className="flex items-end gap-2 rounded-xl px-3 py-2"
              style={{ background: '#0A1A1A', border: '1px solid rgba(79,142,247,0.2)' }}
            >
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
                placeholder={lang === 'fr' ? 'Tapez votre message...' : 'Type your message...'}
                disabled={loading}
                className="flex-1 bg-transparent text-white text-sm font-inter placeholder-gray-500 outline-none resize-none"
                style={{ maxHeight: '80px', minHeight: '20px' }}
              />
              <button
                data-testid="chat-send"
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="p-1.5 rounded-lg transition-all flex-shrink-0"
                style={{
                  background: input.trim() && !loading
                    ? 'linear-gradient(135deg, #4F8EF7, #9B59F5)'
                    : 'rgba(255,255,255,0.05)',
                }}
              >
                {loading ? (
                  <Loader2 size={16} className="text-gray-400 animate-spin" />
                ) : (
                  <Send size={16} className={input.trim() ? 'text-white' : 'text-gray-500'} />
                )}
              </button>
            </div>
            <div className="text-center text-gray-600 text-xs font-inter mt-2">
              Powered by Exevori AI
            </div>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        data-testid="chat-toggle"
        onClick={() => setIsOpen(o => !o)}
        className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)',
          boxShadow: '0 8px 32px rgba(79,142,247,0.4)',
        }}
      >
        {isOpen ? (
          <ChevronDown size={24} className="text-white" />
        ) : (
          <MessageSquare size={24} className="text-white" />
        )}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-[#0A1A1A] animate-pulse" />
        )}
      </button>
    </div>
  );
}
